import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { UserRole } from '@/lib/types/auth';
import type { SidebarItem } from '@/lib/types/sidebar';
import { normalizePath } from './pageExclusions';

export const FULL_ACCESS_ROLES: UserRole[] = [
  UserRole.MasterAdmin,
  UserRole.Owner,
  UserRole.AllAdmin,
];

export function flattenSidebarLinks(items: SidebarItem[]): { path: string; roles: UserRole[] }[] {
  const out: { path: string; roles: UserRole[] }[] = [];
  const walk = (nodes: SidebarItem[]) => {
    for (const node of nodes || []) {
      if (node.type === 'link' && node.path) {
        out.push({ path: node.path, roles: (node.roles || []) as UserRole[] });
      }
      if (node.type === 'folder' && node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(items || []);
  return out;
}

/**
 * Sidebar links with the longest path matching a route (so /sales/store does not use /sales-admin).
 * The same page can be linked from several places, so this can return more than one link.
 */
export function matchingSidebarLinks(
  items: SidebarItem[],
  pathname: string,
): { path: string; roles: UserRole[] }[] {
  const target = normalizePath(pathname);
  if (!target) return [];
  const matches = flattenSidebarLinks(items).filter((link) => {
    const p = normalizePath(link.path);
    if (!p) return false;
    if (p === '/') return target === '/';
    return target === p || target.startsWith(`${p}/`);
  });
  const longest = Math.max(0, ...matches.map((link) => normalizePath(link.path).length));
  return matches.filter((link) => normalizePath(link.path).length === longest);
}

/**
 * True when Settings → sidebar roles grant this path.
 * Empty role lists mean “everyone”, matching Sidebar.hasAccess.
 */
export function sidebarGrantsPath(
  items: SidebarItem[],
  pathname: string,
  userRoles: string[] | undefined | null,
): boolean {
  const roles = userRoles || [];
  if (roles.some((r) => FULL_ACCESS_ROLES.includes(r as UserRole))) return true;
  return matchingSidebarLinks(items, pathname).some(
    (link) => !link.roles.length || roles.some((r) => link.roles.includes(r as UserRole)),
  );
}

type SidebarListener = (items: SidebarItem[]) => void;

let cachedItems: SidebarItem[] | null = null;
let subscribers = new Set<SidebarListener>();
let stopSnapshot: (() => void) | null = null;

function ensureSidebarListener() {
  if (stopSnapshot) return;
  stopSnapshot = onSnapshot(
    doc(db, 'settings', 'sidebar_config'),
    (snap) => {
      const items = (snap.data()?.items as SidebarItem[]) || [];
      cachedItems = items;
      subscribers.forEach((cb) => cb(items));
    },
    () => {
      cachedItems = [];
      subscribers.forEach((cb) => cb([]));
    },
  );
}

/** Shared live subscription to settings/sidebar_config. */
export function subscribeSidebarConfig(cb: SidebarListener): () => void {
  ensureSidebarListener();
  subscribers.add(cb);
  if (cachedItems) cb(cachedItems);
  return () => {
    subscribers.delete(cb);
  };
}
